import { b64Vecs, createShapeId, toRichText, type TLArrowShape, type TLShapeId } from "@tldraw/tlschema";
import { Vec, type VecLike } from "../geometry";
import type { CanvasEditor } from "./editor";
import { StateNode, type PointerEventInfo } from "./state-node";
import { bindTerminal } from "./builtin/arrow";

/** Drag to pan. */
export class HandTool extends StateNode {
  static override id = "hand";
  private start?: { point: Vec; camera: { x: number; y: number; z: number } };
  override onEnter() {
    this.editor.setCursor({ type: "grab", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    this.start = { point: info.point, camera: { ...this.editor.getCamera() } };
    this.editor.setCursor({ type: "grabbing", rotation: 0 });
  }
  override onPointerMove(info: PointerEventInfo) {
    if (!this.start) return;
    const { point, camera } = this.start;
    const d = Vec.Sub(info.point, point);
    this.editor.setCamera({
      x: camera.x + d.x / camera.z,
      y: camera.y + d.y / camera.z,
      z: camera.z,
    });
  }
  override onPointerUp() {
    this.start = undefined;
    this.editor.setCursor({ type: "grab", rotation: 0 });
  }
  override onCancel() {
    this.editor.setCurrentTool("select");
  }
}

/** Click to place text, or drag sideways for a fixed-width frame, then type. */
export class TextTool extends StateNode {
  static override id = "text";
  private origin: Vec | null = null;
  private frameId: TLShapeId | null = null;
  override onEnter() {
    this.editor.setCursor({ type: "text", rotation: 0 });
    this.origin = null;
    this.frameId = null;
  }
  override onPointerDown(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (
      info.target === "shape" &&
      editor.getUtil(info.shape).canEdit?.(info.shape)
    ) {
      this.edit(info.shape.id);
      return;
    }
    if (editor.getIsReadonly()) return;
    this.origin = info.pagePoint.clone();
    this.frameId = null;
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (!this.origin || !editor.inputs.isPointerDown) return;
    const width = Math.abs(info.pagePoint.x - this.origin.x);
    // Short or vertical movement is click jitter, at every zoom.
    if (!this.frameId && width * editor.getZoomLevel() <= TEXT_FRAME_DRAG) return;
    const x = Math.min(this.origin.x, info.pagePoint.x);
    if (!this.frameId) {
      editor.markHistoryStoppingPoint("create text frame");
      this.frameId = createShapeId();
      editor.createShape({
        id: this.frameId,
        type: "text",
        x,
        y: this.origin.y,
        props: { richText: toRichText(""), autoSize: false, w: Math.max(1, width) },
      } as never);
      return;
    }
    const local = editor.getPointInParentSpace(this.frameId, { x, y: this.origin.y });
    editor.updateShape({
      id: this.frameId,
      type: "text",
      x: local.x,
      props: { w: Math.max(1, width) },
    } as never);
  }
  override onPointerUp(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    const origin = this.origin;
    this.origin = null;
    if (!origin || editor.getIsReadonly()) return;
    let id = this.frameId;
    this.frameId = null;
    if (!id) {
      id = createShapeId();
      editor.markHistoryStoppingPoint("create text");
      editor.createShape({
        id,
        type: "text",
        x: info.pagePoint.x,
        y: info.pagePoint.y,
        props: { richText: toRichText("") },
      } as never);
    }
    // The first line centres on the press, for clicks and frames alike.
    const bounds = editor.getShapePageBounds(id);
    if (bounds) {
      const local = editor.getPointInParentSpace(id, { x: bounds.x, y: origin.y - bounds.h / 2 });
      editor.updateShape({ id, type: "text", y: local.y } as never);
    }
    this.edit(id);
  }
  private edit(id: TLShapeId) {
    const editor = this.editor as unknown as CanvasEditor;
    editor.setCurrentTool("select");
    editor.select(id);
    editor.setEditingShape(id);
    editor.setCurrentTool("select.editing_shape");
  }
  override onCancel() {
    if (this.frameId) this.editor.deleteShapes([this.frameId]);
    this.origin = null;
    this.frameId = null;
    this.editor.setCurrentTool("select");
  }
}
const TEXT_FRAME_DRAG = 24; // screen px of sideways drag that makes a text frame

// ----- freehand drawing -----

type StrokeType = "draw" | "highlight";
const CLOSE_DISTANCE = 8; // screen px between start and end that closes a stroke

/** Press and drag to draw a stroke; Shift draws a straight line. */
class StrokeTool extends StateNode {
  static strokeType: StrokeType = "draw";
  private shapeId: TLShapeId | null = null;
  private origin = new Vec();
  private points: Vec[] = [];
  private mark = "";
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (editor.getIsReadonly()) return;
    this.mark = editor.markHistoryStoppingPoint("draw", { hold: true });
    this.origin = info.pagePoint.clone();
    this.points = [new Vec(0, 0, 0.5)];
    this.shapeId = createShapeId();
    editor.createShape({
      id: this.shapeId,
      type: (this.constructor as typeof StrokeTool).strokeType,
      x: this.origin.x,
      y: this.origin.y,
      props: { segments: [{ type: "free", path: b64Vecs.encodePoints(this.points) }] },
    } as never);
  }
  override onPointerMove(info: PointerEventInfo) {
    if (!this.shapeId || !(this.editor as unknown as CanvasEditor).inputs.isPointerDown) return;
    const shape = this.editor.getShape(this.shapeId);
    if (!shape) return;
    const local = new Vec(info.pagePoint.x - this.origin.x, info.pagePoint.y - this.origin.y, 0.5);
    // Positions are page-relative; the shape may have been moved into tracing paper.
    const offset = this.editor.getPointInShapeSpace(shape, this.origin);
    const point = new Vec(local.x + offset.x, local.y + offset.y, 0.5);
    const straight = this.editor.inputs.shiftKey;
    if (straight) this.points = [this.points[0], point];
    else this.points.push(point);
    this.editor.updateShape({
      id: this.shapeId,
      type: shape.type,
      props: { segments: [{ type: straight ? "straight" : "free", path: b64Vecs.encodePoints(this.points) }] },
    } as never);
  }
  override onPointerUp() {
    if (!this.shapeId) return;
    const shape = this.editor.getShape(this.shapeId);
    if (shape) {
      const first = this.points[0],
        last = this.points[this.points.length - 1];
      const closes =
        shape.type === "draw" &&
        this.points.length > 4 &&
        Vec.Dist(first, last) * this.editor.getZoomLevel() < CLOSE_DISTANCE;
      this.editor.updateShape({
        id: this.shapeId,
        type: shape.type,
        props: { isComplete: true, ...(closes ? { isClosed: true } : {}) },
      } as never);
    }
    this.shapeId = null;
    this.editor.commitHistoryStoppingPoint();
  }
  override onCancel() {
    if (this.shapeId) this.editor.bailToMark(this.mark);
    this.shapeId = null;
    this.editor.setCurrentTool("select");
  }
  override onExit() {
    if (this.shapeId) this.editor.bailToMark(this.mark);
    this.shapeId = null;
  }
}
export class DrawTool extends StrokeTool {
  static override id = "draw";
  static override strokeType: StrokeType = "draw";
}
export class HighlightTool extends StrokeTool {
  static override id = "highlight";
  static override strokeType: StrokeType = "highlight";
}

/** Drag across shapes to erase them on release. Sheets and locked shapes stay. */
export class EraserTool extends StateNode {
  static override id = "eraser";
  private erasing = new Set<TLShapeId>();
  private last: Vec | null = null;
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    if (this.editor.getIsReadonly()) return;
    this.erasing = new Set();
    this.last = info.pagePoint.clone();
    this.collect(info.pagePoint);
  }
  override onPointerMove(info: PointerEventInfo) {
    if (!this.last) return;
    // Sample along the drag so fast movement does not skip thin strokes.
    const from = this.last,
      to = info.pagePoint;
    const steps = Math.max(1, Math.ceil(Vec.Dist(from, to) / (4 / this.editor.getZoomLevel())));
    for (let i = 1; i <= steps; i++) this.collect(Vec.Lrp(from, to, i / steps));
    this.last = to.clone();
  }
  private collect(point: VecLike) {
    const editor = this.editor as unknown as CanvasEditor;
    const hit = editor.getShapeAtPoint(point, {
      margin: 4 / editor.getZoomLevel(),
      hitInside: false,
      filter: (s) => !editor.isShapeFrameLike(s) && s.type !== "sheet",
    });
    if (!hit) return;
    const target = editor.getOutermostSelectableShape(hit);
    if (editor.isShapeOrAncestorLocked(target) || this.erasing.has(target.id)) return;
    this.erasing.add(target.id);
    editor.setErasingShapes([...this.erasing]);
  }
  override onPointerUp() {
    const editor = this.editor as unknown as CanvasEditor;
    if (this.erasing.size) {
      editor.markHistoryStoppingPoint("erase");
      editor.deleteShapes([...this.erasing]);
    }
    this.reset();
    editor.commitHistoryStoppingPoint();
  }
  override onCancel() {
    this.reset();
    this.editor.setCurrentTool("select");
  }
  private reset() {
    this.erasing = new Set();
    this.last = null;
    (this.editor as unknown as CanvasEditor).setErasingShapes([]);
  }
}

// ----- geo shapes and notes -----

const DEFAULT_GEO_SIZE = 160;

/** Drag out a geo shape (Shift keeps it square); a click places a default size. */
export class GeoTool extends StateNode {
  static override id = "geo";
  private shapeId: TLShapeId | null = null;
  private origin = new Vec();
  private mark = "";
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    if (this.editor.getIsReadonly()) return;
    this.mark = this.editor.markHistoryStoppingPoint("create geo", { hold: true });
    this.origin = info.pagePoint.clone();
    this.shapeId = null;
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (!editor.inputs.isPointerDown || !editor.inputs.getIsDragging() || this.editor.getIsReadonly()) return;
    let w = info.pagePoint.x - this.origin.x,
      h = info.pagePoint.y - this.origin.y;
    if (editor.inputs.shiftKey) {
      const s = Math.max(Math.abs(w), Math.abs(h));
      w = Math.sign(w || 1) * s;
      h = Math.sign(h || 1) * s;
    }
    const x = Math.min(this.origin.x, this.origin.x + w),
      y = Math.min(this.origin.y, this.origin.y + h);
    const size = { w: Math.max(1, Math.abs(w)), h: Math.max(1, Math.abs(h)) };
    if (!this.shapeId) {
      this.shapeId = createShapeId();
      editor.createShape({ id: this.shapeId, type: "geo", x, y, props: size } as never);
      return;
    }
    // Position in the shape's parent (it may have been placed on tracing paper).
    const local = editor.getPointInParentSpace(this.shapeId, { x, y });
    editor.updateShape({ id: this.shapeId, type: "geo", x: local.x, y: local.y, props: size } as never);
  }
  override onPointerUp(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (this.editor.getIsReadonly()) return;
    let id = this.shapeId;
    if (!id) {
      id = createShapeId();
      editor.createShape({
        id,
        type: "geo",
        x: info.pagePoint.x - DEFAULT_GEO_SIZE / 2,
        y: info.pagePoint.y - DEFAULT_GEO_SIZE / 2,
        props: { w: DEFAULT_GEO_SIZE, h: DEFAULT_GEO_SIZE },
      } as never);
    }
    this.shapeId = null;
    editor.setCurrentTool("select");
    if (editor.getShape(id)) editor.select(id);
  }
  override onCancel() {
    if (this.shapeId) this.editor.bailToMark(this.mark);
    this.shapeId = null;
    this.editor.setCurrentTool("select");
  }
}

/** Click to place a sticky note and start typing. */
export class NoteTool extends StateNode {
  static override id = "note";
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (editor.getIsReadonly()) return;
    editor.markHistoryStoppingPoint("create note");
    const id = createShapeId();
    editor.createShape({ id, type: "note", x: info.pagePoint.x - 100, y: info.pagePoint.y - 100 } as never);
    editor.setCurrentTool("select");
    editor.select(id);
    editor.setEditingShape(id);
    editor.setCurrentTool("select.editing_shape");
  }
  override onCancel() {
    this.editor.setCurrentTool("select");
  }
}

/** Drag to draw an arrow; ends that land on a shape bind to it. */
export class ArrowTool extends StateNode {
  static override id = "arrow";
  private shapeId: TLShapeId | null = null;
  private origin = new Vec();
  private mark = "";
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    if (this.editor.getIsReadonly()) return;
    this.mark = this.editor.markHistoryStoppingPoint("create arrow", { hold: true });
    this.origin = info.pagePoint.clone();
    this.shapeId = null;
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = this.editor as unknown as CanvasEditor;
    if (!editor.inputs.isPointerDown || !editor.inputs.getIsDragging() || editor.getIsReadonly()) return;
    if (!this.shapeId) {
      this.shapeId = createShapeId();
      editor.createShape({
        id: this.shapeId,
        type: "arrow",
        x: this.origin.x,
        y: this.origin.y,
        props: { start: { x: 0, y: 0 }, end: { x: 0, y: 0 } },
      } as never);
      const arrow = editor.getShape(this.shapeId) as TLArrowShape | undefined;
      if (arrow) bindTerminal(editor, arrow, "start", this.origin);
    }
    let point: VecLike = info.pagePoint;
    if (editor.inputs.shiftKey) {
      // Snap to 15° steps.
      const d = Vec.Sub(point, this.origin);
      const step = Math.PI / 12;
      const angle = Math.round(Math.atan2(d.y, d.x) / step) * step;
      const len = d.len();
      point = { x: this.origin.x + Math.cos(angle) * len, y: this.origin.y + Math.sin(angle) * len };
    }
    const arrow = editor.getShape(this.shapeId) as TLArrowShape | undefined;
    if (!arrow) return;
    const local = editor.getPointInShapeSpace(arrow, point);
    editor.updateShape({ id: arrow.id, type: "arrow", props: { end: { x: local.x, y: local.y } } } as never);
    bindTerminal(editor, editor.getShape(arrow.id) as TLArrowShape, "end", Vec.From(point), editor.inputs.ctrlKey);
  }
  override onPointerUp() {
    const editor = this.editor as unknown as CanvasEditor;
    const id = this.shapeId;
    this.shapeId = null;
    editor.setCurrentTool("select");
    if (id && editor.getShape(id)) editor.select(id);
  }
  override onCancel() {
    if (this.shapeId) this.editor.bailToMark(this.mark);
    this.shapeId = null;
    this.editor.setCurrentTool("select");
  }
}
